import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Timestamp,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit as limitar,
  onSnapshot,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { fechaLocalISO } from '../utils/fechaLocal';

/**
 * 🔥 DATOS COMPARTIDOS DE FIRESTORE PARA EL PANEL DE ADMINISTRACIÓN
 *
 * Antes, el Dashboard abría cuatro `onSnapshot` sobre colecciones enteras para
 * mostrar cuatro números, y `FormResponses` mantenía una quinta suscripción a
 * `respuestas_formularios` completa: navegar entre Inicio y Respuestas pagaba
 * dos veces el historial entero.
 *
 * Aquí ese trabajo se hace una sola vez para todo el panel:
 *   · los contadores salen de `getCountFromServer()` (1 lectura por consulta,
 *     no N),
 *   · las respuestas vienen de UNA suscripción acotada por fecha y con `limit`,
 *   · los catálogos (formularios y grupos) y los nombres de usuario se leen
 *     fuera del listener y se cachean.
 *
 * El proveedor envuelve `/admin`, así que la suscripción sobrevive a la
 * navegación entre pantallas: volver al Dashboard no vuelve a leer nada.
 */

/** ⏱️ Cada cuánto, como mucho, se refleja en pantalla lo que llega del servidor. */
export const INTERVALO_REFRESCO_MS = 90 * 1000; // 1,5 minutos

/**
 * 🔢 Techo de respuestas que se descargan de golpe.
 *
 * ⚠️ ESTE NÚMERO NO PUEDE CRECER SIN LÍMITE, y por eso existe el filtro por
 * formulario de aquí abajo. Medido sobre los datos reales tras migrar Kizeo, una
 * respuesta pesa 13,8 KB de media (mediana 8,2 · p95 42 · máx 105), así que las
 * 8.048 del histórico serían ~109 MB por la red y en memoria del navegador, en
 * CADA carga del panel, más 8.048 lecturas de Firestore por suscripción. Mil es
 * lo que se aguanta sin filtrar (~14 MB); para ver más, se acota por formulario
 * y entonces el conjunto nunca pasa del tamaño de un formulario.
 */
export const LIMITE_RESPUESTAS = 1000;

/** 🕒 Cuántos registros muestra la actividad reciente del Dashboard. */
export const LIMITE_ACTIVIDAD = 10;

const CONTADORES_VACIOS = {
  formularios: 0,
  usuarios: 0,
  grupos: 0,
  respuestas: 0,
  respuestasHoy: 0,
  respuestasSemana: 0
};

const DatosFirestoreContext = createContext(null);

/** Fecha 'aaaa-mm-dd' -> Date del inicio de ese día, o null si no hay fecha. */
function inicioDelDia(valor) {
  if (!valor) return null;
  const fecha = new Date(`${valor}T00:00:00`);
  return isNaN(fecha) ? null : fecha;
}

/** 'aaaa-mm-dd' -> último instante de ese día en hora local, o null. */
function finDelDia(valor) {
  if (!valor) return null;
  const fecha = new Date(`${valor}T23:59:59.999`);
  return isNaN(fecha) ? null : fecha;
}

/** Hoy a las 00:00 y el lunes móvil de "esta semana" (hoy incluido = 7 días). */
function limitesDeActividad() {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const semanaAtras = new Date(hoy);
  semanaAtras.setDate(semanaAtras.getDate() - 6);
  return { hoy, semanaAtras };
}

export function DatosFirestoreProvider({ children }) {
  // 🔐 Nada se consulta hasta que hay sesión: sin usuario, Firestore rechaza
  // las lecturas y el panel se llenaría de errores de permisos.
  const [hayUsuario, setHayUsuario] = useState(() => Boolean(auth.currentUser));

  // 🎚️ Qué necesita cada pantalla. Son "pegajosos": una vez encendidos siguen
  // activos mientras se esté dentro de /admin, para no montar y desmontar la
  // suscripción en cada navegación (que es justo lo que se quería evitar).
  const [quiereContadores, setQuiereContadores] = useState(false);
  const [quiereCatalogos, setQuiereCatalogos] = useState(false);
  const [limiteRespuestas, setLimiteRespuestas] = useState(0);

  const [contadores, setContadores] = useState(CONTADORES_VACIOS);
  // Solo se pone en true al activar: los refrescos posteriores son silenciosos
  // (si no, el panel volvería a la pantalla de carga cada minuto y medio).
  const [cargandoContadores, setCargandoContadores] = useState(true);

  const [respuestas, setRespuestas] = useState([]);
  const [cargandoRespuestas, setCargandoRespuestas] = useState(true);
  const [errorRespuestas, setErrorRespuestas] = useState('');

  /**
   * 📋 QUÉ FORMULARIO SE ESTÁ MIRANDO, y por qué está aquí y no en la pantalla.
   *
   * `FormResponses` ya tenía un filtro por formulario, pero lo aplicaba en el
   * navegador SOBRE LO YA DESCARGADO: elegir un formulario no traía sus
   * respuestas, solo escondía las de los demás. Con 8.048 en el histórico eso
   * significaba que las de un formulario concreto podían no estar entre las
   * cargadas y el filtro las enseñaba vacías.
   *
   * Al vivir aquí, el filtro entra en la CONSULTA: el conjunto descargado es el
   * de ese formulario y nada más, así que la búsqueda, el agrupado y la
   * exportación en bloque siguen trabajando sobre todo lo que hay que mirar.
   *
   * ⚠️ Necesita el índice compuesto `formularioId ASC, fechaEnvio DESC` de
   * `firestore.indexes.json`. Sin él, Firestore rechaza la consulta en cuanto se
   * elige un formulario.
   */
  const [formularioSeleccionado, setFormularioSeleccionado] = useState('');

  /** 🔢 idFormulario -> cuántas respuestas tiene. Ver `cargarCatalogos`. */
  const [respuestasPorFormulario, setRespuestasPorFormulario] = useState({});

  const [formulariosInfo, setFormulariosInfo] = useState({});
  const [gruposInfo, setGruposInfo] = useState({});
  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);

  // 👤 uid -> nombre. Se rellena fuera del listener y no se vuelve a pedir.
  const [usuariosInfo, setUsuariosInfo] = useState({});
  const usuariosPedidos = useRef(new Set());

  // 📅 Desde qué fecha se traen las respuestas. Por defecto el último mes: es
  // el rango que enseña la pantalla de respuestas, y así el filtro lo aplica
  // Firestore en vez del navegador.
  const [rangoDesde, setRangoDesde] = useState(() => {
    const hace30Dias = new Date();
    hace30Dias.setMonth(hace30Dias.getMonth() - 1);
    return fechaLocalISO(hace30Dias);
  });

  // 📅 Hasta qué fecha. TIENE que ir en la consulta igual que el "desde": la
  // consulta es "las LIMITE_RESPUESTAS más recientes", así que si el "hasta" se
  // aplicaba en el navegador sobre esas, un rango que acaba antes del último
  // millar salía vacío aunque en Firestore hubiera respuestas en él. Vacío =
  // sin tope, que es lo que mantiene viva la actividad reciente del Dashboard.
  const [rangoHasta, setRangoHasta] = useState('');

  const activarContadores = useCallback(() => setQuiereContadores(true), []);
  const activarCatalogos = useCallback(() => setQuiereCatalogos(true), []);
  const activarRespuestas = useCallback((cuantas) => {
    setLimiteRespuestas(actual => Math.max(actual, cuantas || 0));
  }, []);

  useEffect(() => onAuthStateChanged(auth, usuario => setHayUsuario(Boolean(usuario))), []);

  // ──────────────────────────────────────────────────────────────────────────
  // 🔢 CONTADORES
  // `getCountFromServer` cuenta en el servidor: Firestore lo cobra como una
  // lectura por consulta en vez de una por documento.
  // ──────────────────────────────────────────────────────────────────────────
  const cargarContadores = useCallback(async () => {
    const { hoy, semanaAtras } = limitesDeActividad();
    const respuestasRef = collection(db, 'respuestas_formularios');

    try {
      const [formularios, usuarios, grupos, total, deHoy, deLaSemana] = await Promise.all([
        getCountFromServer(collection(db, 'formularios')),
        getCountFromServer(collection(db, 'usuarios')),
        getCountFromServer(collection(db, 'grupos')),
        getCountFromServer(respuestasRef),
        getCountFromServer(query(respuestasRef, where('fechaEnvio', '>=', Timestamp.fromDate(hoy)))),
        getCountFromServer(query(respuestasRef, where('fechaEnvio', '>=', Timestamp.fromDate(semanaAtras))))
      ]);

      setContadores({
        formularios: formularios.data().count,
        usuarios: usuarios.data().count,
        grupos: grupos.data().count,
        respuestas: total.data().count,
        respuestasHoy: deHoy.data().count,
        respuestasSemana: deLaSemana.data().count
      });
    } catch (error) {
      console.error('Error al contar documentos en Firestore:', error);
    } finally {
      setCargandoContadores(false);
    }
  }, []);

  useEffect(() => {
    if (!hayUsuario || !quiereContadores) return undefined;

    setCargandoContadores(true);
    cargarContadores();

    // Los contadores no son un listener: se refrescan al mismo ritmo lento que
    // el resto del panel.
    const intervalo = setInterval(cargarContadores, INTERVALO_REFRESCO_MS);
    return () => clearInterval(intervalo);
  }, [hayUsuario, quiereContadores, cargarContadores]);

  // ──────────────────────────────────────────────────────────────────────────
  // 📋 RESPUESTAS: una sola suscripción, acotada y con refresco lento
  // ──────────────────────────────────────────────────────────────────────────

  // Buffer del último snapshot que aún no se ha pintado (ver el throttle).
  const pendiente = useRef(null);
  const temporizador = useRef(null);
  const ultimaAplicacion = useRef(0);

  const aplicarRespuestas = useCallback((datos) => {
    pendiente.current = null;
    ultimaAplicacion.current = Date.now();
    setRespuestas(datos);
  }, []);

  useEffect(() => {
    if (!hayUsuario || limiteRespuestas <= 0) return undefined;

    setCargandoRespuestas(true);
    setErrorRespuestas('');

    const desde = inicioDelDia(rangoDesde);
    const hasta = finDelDia(rangoHasta);
    const condiciones = [];
    // 📋 El filtro por formulario va DENTRO de la consulta: ver
    // `formularioSeleccionado`. Es lo que mantiene acotado el conjunto sin
    // romper nada de lo que la pantalla calcula en el navegador.
    if (formularioSeleccionado) condiciones.push(where('formularioId', '==', formularioSeleccionado));
    if (desde) condiciones.push(where('fechaEnvio', '>=', Timestamp.fromDate(desde)));
    if (hasta) condiciones.push(where('fechaEnvio', '<=', Timestamp.fromDate(hasta)));
    condiciones.push(orderBy('fechaEnvio', 'desc'));
    condiciones.push(limitar(limiteRespuestas));

    const consulta = query(collection(db, 'respuestas_formularios'), ...condiciones);
    // "Primera" dura hasta el primer snapshot que viene DEL SERVIDOR: si el
    // primero sale de la caché local (lo que ya hubiera de la consulta
    // anterior), el bueno llegaba segundos después y el throttle lo retenía
    // INTERVALO_REFRESCO_MS, así que cambiar de rango parecía no hacer nada.
    let primera = true;

    const unsubscribe = onSnapshot(
      consulta,
      (snapshot) => {
        const datos = snapshot.docs.map(documento => ({ id: documento.id, ...documento.data() }));

        // ⏱️ El servidor puede empujar cambios continuamente (la APK envía
        // respuestas a cualquier hora). Se pinta como mucho una vez cada
        // INTERVALO_REFRESCO_MS, salvo la primera carga y los cambios que ha
        // hecho el propio administrador, que deben verse al instante.
        const transcurrido = Date.now() - ultimaAplicacion.current;
        if (primera || snapshot.metadata.hasPendingWrites || transcurrido >= INTERVALO_REFRESCO_MS) {
          if (!snapshot.metadata.fromCache) primera = false;
          aplicarRespuestas(datos);
          setCargandoRespuestas(false);
          return;
        }

        pendiente.current = datos;
        if (!temporizador.current) {
          temporizador.current = setTimeout(() => {
            temporizador.current = null;
            if (pendiente.current) aplicarRespuestas(pendiente.current);
          }, INTERVALO_REFRESCO_MS - transcurrido);
        }
      },
      (error) => {
        console.error('Error al escuchar respuestas:', error);
        // 🗂️ FALTA EL ÍNDICE: el error de Firestore es un párrafo con un enlace
        // dentro, y en pantalla no se entiende. Filtrar por formulario necesita
        // el índice compuesto de `firestore.indexes.json`; si el proyecto se
        // despliega sin él, esto dice exactamente qué hacer en vez de dejar al
        // administrador con un mensaje en inglés sobre una consulta.
        setErrorRespuestas(
          error.code === 'failed-precondition'
            ? 'Falta un índice de Firestore para filtrar por formulario. ' +
              'Despliégalo con "npx firebase deploy --only firestore:indexes" ' +
              '(o abre el enlace que aparece en la consola del navegador) y vuelve a entrar.'
            : error.message
        );
        setCargandoRespuestas(false);
      }
    );

    return () => {
      unsubscribe();
      if (temporizador.current) {
        clearTimeout(temporizador.current);
        temporizador.current = null;
      }
      pendiente.current = null;
    };
  }, [hayUsuario, limiteRespuestas, rangoDesde, rangoHasta, formularioSeleccionado, aplicarRespuestas]);

  // ──────────────────────────────────────────────────────────────────────────
  // 🗂️ CATÁLOGOS: formularios y grupos completos, una vez por sesión de panel
  // ──────────────────────────────────────────────────────────────────────────
  const cargarCatalogos = useCallback(async () => {
    try {
      const [formulariosSnap, gruposSnap] = await Promise.all([
        getDocs(collection(db, 'formularios')),
        getDocs(collection(db, 'grupos'))
      ]);

      const formularios = {};
      formulariosSnap.forEach(documento => { formularios[documento.id] = documento.data(); });
      setFormulariosInfo(formularios);

      const grupos = {};
      gruposSnap.forEach(documento => { grupos[documento.id] = documento.data(); });
      setGruposInfo(grupos);

      // 🔢 CUÁNTAS RESPUESTAS TIENE CADA FORMULARIO.
      //
      // El desplegable de "formulario" sacaba sus opciones de las respuestas YA
      // DESCARGADAS, así que solo ofrecía los formularios que aparecían en el
      // último millar: los demás no se podían ni elegir, que es justo lo que
      // hace falta para acotar la consulta. Aquí se pregunta por todos.
      //
      // Es una lectura por formulario y no una por documento: `count()` lo
      // resuelve el servidor. Con 51 formularios son 51 lecturas una vez por
      // sesión de panel, y de paso da el número que se enseña en cada opción.
      const conteos = await Promise.all(
        Object.keys(formularios).map(async id => {
          try {
            const resultado = await getCountFromServer(
              query(collection(db, 'respuestas_formularios'), where('formularioId', '==', id))
            );
            return [id, resultado.data().count];
          } catch (error) {
            // Que falle un conteo no puede dejar sin catálogo al panel entero:
            // ese formulario simplemente no dirá cuántas tiene.
            console.error('No se pudo contar las respuestas de', id, error);
            return [id, 0];
          }
        })
      );
      setRespuestasPorFormulario(Object.fromEntries(conteos));
    } catch (error) {
      console.error('Error al cargar formularios y grupos:', error);
    } finally {
      setCargandoCatalogos(false);
    }
  }, []);

  useEffect(() => {
    if (!hayUsuario || !quiereCatalogos) return;
    setCargandoCatalogos(true);
    cargarCatalogos();
  }, [hayUsuario, quiereCatalogos, cargarCatalogos]);

  /**
   * 🔄 Pinta ya lo que hubiera en espera, vuelve a contar y relee los
   * catálogos: son la única parte que no está suscrita, así que este es el
   * modo de ver un formulario recién editado sin recargar la página.
   */
  const refrescar = useCallback(() => {
    if (temporizador.current) {
      clearTimeout(temporizador.current);
      temporizador.current = null;
    }
    if (pendiente.current) aplicarRespuestas(pendiente.current);
    if (quiereContadores) cargarContadores();
    if (quiereCatalogos) cargarCatalogos();
  }, [aplicarRespuestas, cargarContadores, cargarCatalogos, quiereContadores, quiereCatalogos]);

  // ──────────────────────────────────────────────────────────────────────────
  // 👤 NOMBRES DE USUARIO
  // Fuera del listener y en paralelo: dentro se leían en serie y se repetían
  // enteros con cada snapshot.
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hayUsuario) return;

    const faltan = [...new Set(respuestas.map(respuesta => respuesta.usuarioId).filter(Boolean))]
      .filter(uid => !usuariosPedidos.current.has(uid));
    if (faltan.length === 0) return;

    // Se marcan antes de pedirlos para que un segundo snapshot no los repita
    faltan.forEach(uid => usuariosPedidos.current.add(uid));

    (async () => {
      const encontrados = await Promise.all(faltan.map(async (uid) => {
        try {
          const usuarioDoc = await getDoc(doc(db, 'usuarios', uid));
          if (!usuarioDoc.exists()) return [uid, uid];
          const datos = usuarioDoc.data();
          return [uid, datos.nombre || datos.email || uid];
        } catch (error) {
          console.error('Error al obtener usuario:', uid, error);
          return [uid, uid];
        }
      }));

      setUsuariosInfo(previos => ({ ...previos, ...Object.fromEntries(encontrados) }));
    })();
  }, [hayUsuario, respuestas]);

  /** 🕒 Últimos registros para el Dashboard (la consulta ya viene ordenada). */
  const actividadReciente = useMemo(
    () => respuestas.slice(0, LIMITE_ACTIVIDAD),
    [respuestas]
  );

  /** 📛 id de formulario -> nombre visible. */
  const nombresFormulario = useMemo(() => {
    const nombres = {};
    Object.entries(formulariosInfo).forEach(([id, datos]) => {
      nombres[id] = datos?.nombre || datos?.titulo || id;
    });
    return nombres;
  }, [formulariosInfo]);

  const valor = useMemo(() => ({
    contadores,
    cargandoContadores,
    respuestas,
    actividadReciente,
    cargandoRespuestas,
    errorRespuestas,
    limiteRespuestas,
    rangoDesde,
    setRangoDesde,
    rangoHasta,
    setRangoHasta,
    formularioSeleccionado,
    setFormularioSeleccionado,
    respuestasPorFormulario,
    formulariosInfo,
    gruposInfo,
    nombresFormulario,
    cargandoCatalogos,
    usuariosInfo,
    refrescar,
    recargarCatalogos: cargarCatalogos,
    activarContadores,
    activarCatalogos,
    activarRespuestas
  }), [
    contadores, cargandoContadores, respuestas, actividadReciente, cargandoRespuestas,
    errorRespuestas, limiteRespuestas, rangoDesde, rangoHasta, formularioSeleccionado,
    respuestasPorFormulario, formulariosInfo, gruposInfo,
    nombresFormulario, cargandoCatalogos, usuariosInfo, refrescar, cargarCatalogos,
    activarContadores, activarCatalogos, activarRespuestas
  ]);

  return (
    <DatosFirestoreContext.Provider value={valor}>
      {children}
    </DatosFirestoreContext.Provider>
  );
}

/**
 * Acceso a los datos compartidos. Cada pantalla declara qué necesita:
 *
 *   useDatosFirestore({ contadores: true })            // solo los números
 *   useDatosFirestore({ respuestas: 500, catalogos: true })
 *
 * `respuestas` es el número máximo de registros que quiere esa pantalla; el
 * proveedor se queda con el mayor de los pedidos.
 */
export function useDatosFirestore({ contadores = false, catalogos = false, respuestas = 0 } = {}) {
  const contexto = useContext(DatosFirestoreContext);
  if (!contexto) {
    throw new Error('useDatosFirestore debe usarse dentro de <DatosFirestoreProvider>');
  }

  const { activarContadores, activarCatalogos, activarRespuestas } = contexto;

  useEffect(() => {
    if (contadores) activarContadores();
  }, [contadores, activarContadores]);

  useEffect(() => {
    if (catalogos) activarCatalogos();
  }, [catalogos, activarCatalogos]);

  useEffect(() => {
    if (respuestas > 0) activarRespuestas(respuestas);
  }, [respuestas, activarRespuestas]);

  return contexto;
}

export default DatosFirestoreContext;
